// The Live screen: the conversation as it happened, every worker at a
// glance, and the gate folded in — the hub page's Live tab rebuilt on
// the session store (spec 03.2). The conversation and the workers read
// the store; the gate answers through the command path (Connect unary),
// optimistically in the store. Turn traffic is not state: the acting
// line under the header listens to the frames straight off the client.
import { createEffect, createMemo, createSignal, For, Index, on, onCleanup, onMount, Show, type JSX } from "solid-js";
import { PendingAnswer } from "@/gen/psypen/v1/gate_pb";
import { HistoryKind } from "@/gen/psypen/v1/session_pb";
import type { Session } from "@/session/client";
import type { StoredHistoryEvent, StoredPendingSend } from "@/session/types";
import { answerAtGate, type GateRpc } from "@/live/gate";
import { groupTurns, workerCard } from "@/live/model";
import { gateRpc } from "@/rpc";
import { t } from "@/i18n";
import { upload, UploadError, uploadSummary } from "@/live/upload";

function time(at: string | null): string {
  if (!at) return "";
  try {
    return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  } catch {
    return "";
  }
}

// The one word the worker's state shows, in the window's language when
// it has one; the server's own words are the contract's set (working,
// needs you, sleeping, failed, idle, done).
function stateWord(state: string): string {
  return t(`live.state.${state}`, state);
}

// The state's class key: the contract's words carry spaces ("needs
// you"), a class cannot.
function stateClass(state: string): string {
  return state.replace(/\s+/g, "-").toLowerCase() || "idle";
}

function sendLabel(s: StoredPendingSend): string {
  const to = s.target ? ` → ${s.target}` : "";
  return `${s.tool || "send"}${to}`;
}

// One stored event as the conversation shows it. The kinds are the
// contract's; the shape follows the hub page's stream.
function eventView(ev: StoredHistoryEvent): JSX.Element {
  switch (ev.kind) {
    case HistoryKind.SAID:
      return <div class="said">{ev.text}</div>;
    case HistoryKind.REPLY:
      return <div class="reply">{ev.text}</div>;
    case HistoryKind.NEEDS:
      return (
        <div class="needs-ev">
          <span class="evlabel">{t("live.waitingfor", "Waiting for you: ")}</span>
          {ev.text}
        </div>
      );
    case HistoryKind.FAILED:
      return <div class="failed-line">{ev.text || t("live.turnfailed", "The turn failed.")}</div>;
    case HistoryKind.TASK:
    case HistoryKind.PROGRESS:
      return <div class="taskline">{ev.text}</div>;
    case HistoryKind.NOTED:
      return <div class="noteline">{ev.text}</div>;
    case HistoryKind.INSERTED:
      return (
        <div class="typed">
          <div class="evlabel">{t("live.inserted", "Typed into the field")}</div>
          <pre>{ev.text}</pre>
        </div>
      );
    case HistoryKind.TOOL:
      return (
        <details class="toolev" open={ev.error}>
          <summary>
            {ev.tool}
            <Show when={ev.error}>
              {" "}
              <span class="errword">{t("live.failedword", "failed")}</span>
            </Show>
          </summary>
          <Show when={ev.input}>
            <pre>{ev.input}</pre>
          </Show>
          <Show when={ev.text}>
            <pre>{ev.text}</pre>
          </Show>
        </details>
      );
    default:
      return <div class="noteline">{ev.text}</div>;
  }
}

export function Live(props: { session: Session; gate?: GateRpc }): JSX.Element {
  const rpc = () => props.gate ?? gateRpc();
  let root: HTMLElement | undefined;

  // The conversation, folded once per change of the history: the blocks
  // are minted anew, so the list is keyed by position (the stored
  // events inside keep their own reference keys), and the conversation's
  // DOM stands while it grows.
  const turns = createMemo(() => groupTurns(props.session.state().history));

  // What is happening now: a tool call in flight (turn traffic, not
  // state), else the task's own line, else nothing.
  const [acting, setActing] = createSignal("");
  onMount(() => {
    const off = props.session.onFrame((d) => {
      switch (d.msg.case) {
        case "acting":
          setActing(d.msg.value.what);
          break;
        case "reply":
        case "finished":
        case "error":
        case "needs":
          setActing("");
          break;
      }
    });
    onCleanup(off);
  });

  // The conversation keeps up while it is at the bottom, as the hub's
  // stream does; a scroll of one's own leaves it where the user put it.
  // The app scrolls its main, never the document (the shell's layout).
  let opened = false;
  const stick = () => {
    const el = root?.closest("main");
    if (!el) return;
    if (!opened || el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      el.scrollTop = el.scrollHeight;
    }
  };
  onMount(() => {
    stick();
    opened = true;
  });
  createEffect(on(() => props.session.state().history.length, stick));

  // Files into the workspace: the picker, the camera (touch screens
  // only: desktop browsers ignore capture) and a drop on the screen all
  // come here. One line says what landed, or why it did not.
  let fileInput: HTMLInputElement | undefined;
  let cameraInput: HTMLInputElement | undefined;
  const coarse = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  const [sending, setSending] = createSignal(false);
  const [uploadNote, setUploadNote] = createSignal("");
  const [uploadErr, setUploadErr] = createSignal("");
  async function addFiles(list: FileList | null | undefined): Promise<void> {
    const picked = list ? Array.from(list) : [];
    if (picked.length === 0) return;
    if (sending()) {
      setUploadNote(t("live.upload.busy", "Wait, the last upload is still going."));
      return;
    }
    setSending(true);
    setUploadNote("");
    setUploadErr("");
    try {
      setUploadNote(uploadSummary(await upload(picked)));
    } catch (e) {
      const status = e instanceof UploadError ? e.status : 0;
      setUploadErr(
        status === 413
          ? t("live.upload.toobig", "Too big, or too many files at once.")
          : status === 409
            ? t("live.upload.waiting", "The workspace has too much waiting — hire a worker or remove uploads.")
            : t("live.upload.failed", "The upload did not go through — try again."),
      );
    } finally {
      setSending(false);
    }
  }

  // A file dropped anywhere on the window is for the workspace: the
  // browser would otherwise navigate to it. Only while Live is mounted.
  onMount(() => {
    const over = (e: DragEvent) => e.preventDefault();
    const drop = (e: DragEvent) => {
      e.preventDefault();
      void addFiles(e.dataTransfer?.files);
    };
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    onCleanup(() => {
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
    });
  });

  // The gate: answering is one round trip on the command path; the box
  // itself is already gone the moment the button lands (the store's
  // optimistic hand), so busy only guards a double click.
  const [busy, setBusy] = createSignal(false);
  const [gateError, setGateError] = createSignal("");
  async function answer(a: PendingAnswer): Promise<void> {
    const pending = props.session.state().pending;
    if (!pending || busy()) return;
    setBusy(true);
    setGateError("");
    const res = await answerAtGate(props.session.store, rpc(), pending, a);
    if (!res.ok) setGateError(t("live.gate.failed", "The answer did not go through — try again."));
    setBusy(false);
  }

  return (
    <section class="screen live" ref={root}>
      <h1>{t("tab.live", "Live")}</h1>

      <div class="upload">
        <button type="button" disabled={sending()} onClick={() => fileInput?.click()}>
          {t("live.upload.add", "Add files")}
        </button>
        <Show when={coarse}>
          <button type="button" disabled={sending()} onClick={() => cameraInput?.click()}>
            {t("live.upload.camera", "Photo")}
          </button>
        </Show>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            void addFiles(e.currentTarget.files);
            e.currentTarget.value = "";
          }}
        />
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            void addFiles(e.currentTarget.files);
            e.currentTarget.value = "";
          }}
        />
        <Show when={sending()}>
          <span class="hint">{t("live.upload.sending", "Uploading…")}</span>
        </Show>
      </div>
      <Show when={uploadNote()}>
        <p class="hint">{uploadNote()}</p>
      </Show>
      <Show when={uploadErr()}>
        <p class="err">{uploadErr()}</p>
      </Show>

      <Show when={props.session.state().pending}>
        {(pending) => (
          <div class="card gate" role="region" aria-live="polite" aria-label={t("live.gate.waiting", "Waiting for you")}>
            <h2>{t("live.gate.waiting", "Waiting for you")}</h2>
            <Show when={pending().line}>
              <p class="gate-line">{pending().line}</p>
            </Show>
            <For each={pending().sends}>
              {(s) => (
                <div class="gate-send">
                  <div class="to">{sendLabel(s)}</div>
                  <Show when={s.subject}>
                    <div class="subject">{s.subject}</div>
                  </Show>
                  <pre>{s.text}</pre>
                  <Show when={s.editable}>
                    <p class="hint">{t("live.gate.editable", "To change the draft, say what to change.")}</p>
                  </Show>
                </div>
              )}
            </For>
            <Show
              when={pending().kind === ""}
              fallback={
                <p class="hint">
                  {t(`live.gate.${pending().kind}`, pending().kind)} — {t("live.gate.elsewhere", "answered from the sessions screen")}
                </p>
              }
            >
              <div class="row">
                <button type="button" class="primary" disabled={busy()} onClick={() => void answer(PendingAnswer.SEND)}>
                  {t("live.gate.send", "Send")}
                </button>
                <button type="button" disabled={busy()} onClick={() => void answer(PendingAnswer.CANCEL)}>
                  {t("live.gate.cancel", "Cancel")}
                </button>
                <Show when={pending().expiresAt}>
                  <span class="until">
                    {t("live.gate.until", "until")} {time(pending().expiresAt)}
                  </span>
                </Show>
              </div>
            </Show>
            <Show when={gateError()}>
              <p class="err">{gateError()}</p>
            </Show>
          </div>
        )}
      </Show>

      <div class="now" classList={{ idle: !acting() && !props.session.state().task?.text }}>
        {acting() || props.session.state().task?.text || t("live.now.idle", "Nothing is happening right now.")}
      </div>

      <h2 class="sub">{t("live.workers", "Workers")}</h2>
      <Show
        when={props.session.state().lines.length > 0}
        fallback={<p>{t("live.nolines", "No lines yet.")}</p>}
      >
        <div class="workers">
          <For each={props.session.state().lines}>
 {(line) => {
              const w = createMemo(() => workerCard(line));
              return (
                <article class="worker">
                  <h3>
                    <span class="name">{w().name}</span>
                    <span class={`stateword ${stateClass(w().state)}`}>{stateWord(w().state)}</span>
                  </h3>
                  <Show when={w().goal}>
                    <div class="goal">{w().goal}</div>
                  </Show>
                  <Show when={w().last}>
                    <div class="last">{w().last}</div>
                  </Show>
                  <Show when={w().need}>
                    <div class="need">{w().need}</div>
                  </Show>
                  <Show when={w().handed.length > 0}>
                    <div class="meta">
                      <For each={w().handed}>
                        {(child) => (
                          <a href={child.url} target="_blank" rel="noreferrer">
                            {child.title || child.url}
                          </a>
                        )}
                      </For>
                    </div>
                  </Show>
                  <div class="meta">
                    <Show when={w().open.length > 1}>
                      <span>
                        +{w().open.length - 1} {t("live.more", "more")}
                      </span>
                    </Show>
                    <Show when={w().doneCount > 0}>
                      <span>
                        {w().doneCount} {t("live.donecount", "done")}
                      </span>
                    </Show>
                    <Show when={w().unread > 0}>
                      <span>
                        {w().unread} {t("live.unread", "unread")}
                      </span>
                    </Show>
                  </div>
                </article>
              );
            }}
          </For>
        </div>
      </Show>

      <h2 class="sub">{t("live.conv", "Conversation")}</h2>
      <div class="conv">
        <Index each={turns()}>
          {(block) => (
            <section class="turn">
              <div class="when">{time(block().at)}</div>
              <For each={block().events}>{(ev) => eventView(ev)}</For>
            </section>
          )}
        </Index>
      </div>
    </section>
  );
}
